import React, { useEffect, useState } from "react";
import { Input, Table, Space, Typography, Tooltip } from "antd";
import { Row, Col, Modal, Button, Form } from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWorkoutTemplates,
  updatePagination,
  deleteWorkoutTemplate,
  resetToastConfig,
  fetchWorkoutTemplateById,
  filterWorkoutTemplates,
} from "../../redux/Slices/workoutSlice";
import ToastNotification from "../../components/Common/ToastNotification";
import FormSelect from "../../components/Common/FormSelect";
import { isValidAlphaNumeric } from "../../utils/Utils";
import Loader from "../../components/Common/Loader";

const { Text } = Typography;

const WorkoutTemplate = () => {
  const { trans } = useCustomTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [errors, setErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(false);

  const { workoutTemplates, totalCount, loading, pagination, toastConfig } =
    useSelector((state) => state.workouts);
  const { profileData } = useSelector((state) => state.profile);
  const coachId = profileData?.coachId;

  const [formData, setFormData] = useState({
    templateId: null,
    name: "",
    description: "",
    numberOfSessions: 0,
    startFrom: "blank",
  });

  useEffect(() => {
    dispatch(
      fetchWorkoutTemplates({
        coachId,
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    if (toastConfig && toastConfig.type === "success") {
      const timer = setTimeout(() => {
        dispatch(resetToastConfig());
      }, 5000);

      dispatch(resetToastConfig());
      return () => clearTimeout(timer);
    }
  }, [toastConfig, dispatch]);

  // Handle Search Input
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchText.trim() === "") {
        dispatch(
          fetchWorkoutTemplates({
            coachId,
            pageNumber: pagination.current,
            pageSize: pagination.pageSize,
          })
        );
      } else {
        dispatch(filterWorkoutTemplates({ coachId, name: searchText }));
      }
    }, 300);

    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, dispatch]);

  const handleDeleteClick = (record, e) => {
    e.stopPropagation();
    setTemplateToDelete(record.id);
    setShowDeleteModal(true);
  };

  const handleTableChange = (paginationConfig) => {
    dispatch(
      updatePagination({
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      })
    );
  };

  const handleConfirmDelete = () => {
    if (!templateToDelete) return;

    setIsPageLoading(true);

    dispatch(deleteWorkoutTemplate(templateToDelete));

    const updatedTotal = totalCount - 1;
    const maxPage = Math.ceil(updatedTotal / pagination.pageSize);
    const newPage = pagination.current > maxPage ? maxPage : pagination.current;

    dispatch(
      fetchWorkoutTemplates({
        coachId,
        pageNumber: newPage,
        pageSize: pagination.pageSize,
      })
    );

    dispatch(updatePagination({ current: newPage }));

    setIsPageLoading(false);
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const getShowingText = () => {
    const start = (pagination.current - 1) * pagination.pageSize + 1;
    const end = Math.min(pagination.current * pagination.pageSize, totalCount);

    return totalCount
      ? `${trans("Common.showing")} ${start} ${trans(
          "Common.to"
        )} ${end} ${trans("Common.of")} ${totalCount} ${trans(
          "Common.results"
        )}`
      : trans("Common.noResults");
  };

  const handleAdd = () => {
    setFormData({
      templateId: null,
      name: "",
      description: "",
      numberOfSessions: 0,
      startFrom: "blank",
    });
    setShowModal(true);
  };

  const columns = [
    {
      title: trans("Template.Description"),
      dataIndex: "description",
      key: "description",
      width: "40%",
      className: "template-description-column padded-column",
    },
    {
      title: trans("Template.NoOfSessions"),
      dataIndex: "numberOfSessions",
      key: "numberOfSessions",
      width: "40%",
      className: "template-sessions-column",
    },
    {
      title: trans("Common.action"),
      key: "action",
      className: "action-column",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={trans("Common.edit")}>
            <Button
              type="button"
              className="action-buttons"
              onClick={() => {
                handleEditTemplate(record.id);
              }}
            >
              <i className="bi bi-pencil"></i>
            </Button>
          </Tooltip>
          <Tooltip title={trans("Common.delete")}>
            <Button
              type="button"
              className="action-buttons"
              onClick={(e) => handleDeleteClick(record, e)}
            >
              <BsTrash />
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleCreateAndOpenBuilder = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = trans("Toolbox.planNameRequired");
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setShowModal(false);

    if (formData.startFrom === "blank") {
      navigate("/toolbox/builder", {
        state: { templateName: formData.name, isSelectedFromClient: false },
      });
    } else if (formData.startFrom === "template") {
      setShowTemplateModal(true);
    }
  };

  const handleEditTemplate = (templateId) => {
    setIsPageLoading(true);

    dispatch(fetchWorkoutTemplateById(templateId))
      .unwrap()
      .then((templateData) => {
        const prefilledSessions = templateData.sessions.map((session) => ({
          id: session.workoutSessionID,
          sessionImage: session.sessionPhotoImage,
          name: session.sessionName,
          sessionBlobUrl: session.sessionPhotoImage?.blobUrl || null,
          exercises: session.exercises.map((exercise) => ({
            exerciseId: exercise.exerciseID,
            name: exercise.exerciseName,
            sets: exercise.sets || "1",
            reps: exercise.reps || "1",
            sec: exercise.sec || "",
            setType: exercise.setType || "",
            blobUrl: exercise.exerciseImage?.blobUrl || null,
            exerciseNote: exercise.exerciseNote || "",
            indexOrder: exercise.indexOrder,
          })),
          indexOrder: session.indexOrder,
        }));
        navigate("/toolbox/builder", {
          state: {
            templateCoverPhotoImage: templateData?.coverPhotoImage,
            templateName: templateData.description || "",
            sessions: prefilledSessions,
            templateId: templateData.workoutTemplateID,
            isEditing: true,
            isSelectedFromClient: false,
          },
        });
      })
      .catch((error) => {
        console.error("Error fetching template details:", error);
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name === "name" && !isValidAlphaNumeric(value)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateTemplate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = trans("Toolbox.planNameRequired");
    }
    if (!formData.templateId) {
      newErrors.templateId = "Please select a template";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }
    setIsPageLoading(true);
    setShowTemplateModal(false);

    // Fetch template details before navigating
    dispatch(fetchWorkoutTemplateById(formData.templateId))
      .unwrap()
      .then((templateData) => {
        const prefilledSessions = templateData.sessions.map((session) => ({
          id: session.workoutSessionID,
          name: session.sessionName,
          exercises: session.exercises.map((exercise) => ({
            exerciseId: exercise.exerciseID,
            name: exercise.exerciseName,
            sets: exercise.sets || "1",
            reps: exercise.reps || "1",
            sec: exercise.sec || "",
            setType: exercise.setType || "",
            blobUrl: exercise.exerciseImage?.blobUrl || null,
            exerciseNote: exercise.exerciseNote || "",
            indexOrder: exercise.indexOrder,
          })),
        }));

        navigate("/toolbox/builder", {
          state: {
            templateName: formData.name,
            sessions: prefilledSessions,
            isSelectedFromClient: false,
          },
        });
      })
      .catch((error) => {
        console.error("Error fetching template details:", error);
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  if (isPageLoading) {
    return <Loader />;
  }

  return (
    <div className="template-container table-container">
      {/* Header */}
      <Row className="pb-2">
        <Col>
          <h5 className="pb-2">
            {trans("Template.Template") + " " + trans("Common.details")}
          </h5>
        </Col>
        <Col className="d-flex justify-content-end">
          <Button className="add-button" onClick={handleAdd}>
            <i className="bi bi-plus"></i> {trans("Common.create")}
            {trans("Template.Template")}
          </Button>
        </Col>
      </Row>

      {/* Search Bar */}
      <Row className="mb-2">
        <Col xs={12} md={6}>
          <Input
            value={searchText}
            onChange={handleSearchChange}
            placeholder={trans("Common.searchByName")}
            allowClear
            className="search-box"
          />
        </Col>
      </Row>

      {/* Table & Pagination */}
      <div>
        <Table
          className="custom-table"
          columns={columns}
          dataSource={workoutTemplates}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "75", "100"],
            position: ["bottomRight"],
            showTotal: () => <Text>{getShowingText()}</Text>,
          }}
          onChange={handleTableChange}
          rowKey={(record) => record.id}
          bordered={false}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                }}
              />
            ),
            header: {
              cell: (props) => {
                return (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      background: "transparent",
                      borderColor: "#FCFBFFCC",
                      fontFamily: "Proxima-Nova-Regular",
                      borderStyle: "solid",
                      borderWidth: "8px 0px",
                      padding: "12px 16px",
                      // Border radius for header cells
                      borderRadius:
                        props.children === columns[0].title
                          ? "12px 0 0 12px"
                          : props.children === columns[columns.length - 1].title
                          ? "0 12px 12px 0"
                          : "0",
                    }}
                  />
                );
              },
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    fontFamily: "Proxima-Nova-Regular",
                    background: "transparent",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    padding: "12px 16px",
                    // Border radius for body cells
                    borderRadius:
                      props.index % columns.length === 0
                        ? "12px 0 0 12px"
                        : props.index % columns.length === columns.length - 1
                        ? "0 12px 12px 0"
                        : "0",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    margin: "10px 0",
                  }}
                />
              ),
            },
          }}
        />
      </div>

      {/* Create New Template Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create new template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="template-form">
            {/* Plan Name */}
            <Form.Group controlId="planName">
              <Form.Label>
                {trans("Toolbox.planName")} <span className="required">*</span>
              </Form.Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFieldChange}
                className={`plan-name-input ${errors.name ? "is-invalid" : ""}`}
                placeholder="Enter plan name"
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </Form.Group>

            {/* Plan Selection */}
            <div className="plan-selection-container">
              <div
                className={`plan-option ${
                  formData.startFrom === "blank" ? "selected" : ""
                }`}
                onClick={() => setFormData({ ...formData, startFrom: "blank" })}
              >
                <i className="icon-blank-plan"></i>
                <span>Start from a blank plan</span>
                <input
                  type="radio"
                  checked={formData.startFrom === "blank"}
                  readOnly
                />
              </div>

              <div
                className={`plan-option ${
                  formData.startFrom === "template" ? "selected" : ""
                }`}
                onClick={() =>
                  setFormData({ ...formData, startFrom: "template" })
                }
              >
                <i className="icon-template"></i>
                <span>Start from a template</span>
                <input
                  type="radio"
                  checked={formData.startFrom === "template"}
                  readOnly
                />
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            //variant="outline-primary"
            className="cancel-button"
            onClick={() => setShowModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="create-button"
            onClick={handleCreateAndOpenBuilder}
          >
            Create and open builder
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {trans("Common.confirmation")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{trans("Common.areYouSureDeleteTemplate")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowDeleteModal(false)}
            className="back_button w-25 p-1"
          >
            {trans("Common.cancel")}
          </Button>
          <Button
            className="saveButton w-25 ml-2"
            onClick={handleConfirmDelete}
          >
            {trans("Common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Template Details Modal */}
      <Modal
        show={showTemplateModal}
        onHide={() => setShowTemplateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Client Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="templateSelection" className="mb-2">
              <Form.Label>Select a template (optional)</Form.Label>
              <FormSelect
                name="templateId"
                value={formData.templateId}
                onChange={(e) =>
                  setFormData({ ...formData, templateId: e.target.value })
                }
                options={[
                  { value: "", label: "Select a template" },
                  ...workoutTemplates.map((template) => ({
                    value: template.id,
                    label: template.description,
                  })),
                ]}
              />
            </Form.Group>

            <Form.Group controlId="planName" className="mb-2">
              <Form.Label></Form.Label>
              <Form.Label>
                Client plan name <span className="required">*</span>
              </Form.Label>
              <Input
                type="text"
                value={formData.name}
                onChange={handleFieldChange}
                className={`plan-name-input ${errors.name ? "is-invalid" : ""}`}
                placeholder="Enter plan name"
              />
              {errors.name && (
                <div className="invalid-feedback">{errors.name}</div>
              )}
            </Form.Group>

            <Form.Group controlId="description" className="mb-2">
              <Form.Label>Description</Form.Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="The purpose of this plan is..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowTemplateModal(false)}
          >
            Cancel
          </Button>
          <Button className="create-button" onClick={handleCreateTemplate}>
            Create Client Plan
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && toastConfig.type === "success" && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default WorkoutTemplate;
